/**
 * Places domain — chain/door detail hooks.
 *
 * Hand-written `useQuery` wrappers around the generated retrieve client
 * functions. They preserve the behaviour of the former `api.useGetLodging`
 * family: `enabled` guards on a truthy id, and retries skip a genuine 404.
 * Error toasts are handled by `apiClient` → `baseAPICall`, so no manual
 * `notifyError` is needed here.
 *
 * Query keys mirror the legacy ones (`['lodging', id]` / `['convenience',
 * id]`) — nothing invalidates these keys, so reproducing them keeps cache
 * behaviour identical.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import axios, { type AxiosError } from 'axios'

import type {
  ConvenienceChainProxy,
  LodgingChainProxy,
} from '../../models/google_place_chain'
import type {
  ConvenienceDoorProxy,
  LodgingDoorProxy,
} from '../../models/google_place_door'
import client from '../apiClient'

/**
 * Retry predicate matching the legacy detail hooks: no retry on a 404
 * (the record is genuinely missing); otherwise retry up to 3 times.
 */
function retryExcept404(
  failureCount: number,
  error: Error | AxiosError
): boolean {
  return axios.isAxiosError(error) && error?.response?.status === 404
    ? false
    : failureCount < 3
}

export function useGetLodging(
  lodgingId?: number
): UseQueryResult<LodgingChainProxy, Error> {
  return useQuery({
    queryKey: ['lodging', lodgingId],
    // FIR-7878: unified chain detail endpoint (byte-identical response).
    queryFn: async () =>
      (
        await client<LodgingChainProxy>({
          method: 'GET',
          url: `/api/v1/opportunities/detail/${lodgingId}/`,
          params: { target_type: 'lodging' },
        })
      ).data,
    enabled: !!lodgingId,
    retry: retryExcept404,
  })
}

export function useGetLodgingDoor(
  lodgingDoorId?: number
): UseQueryResult<LodgingDoorProxy, Error> {
  return useQuery({
    queryKey: ['lodging', lodgingDoorId],
    // FIR-7878: unified door detail endpoint (byte-identical response).
    queryFn: async () =>
      (
        await client<LodgingDoorProxy>({
          method: 'GET',
          url: `/api/v1/opportunities/doors/detail/${lodgingDoorId}/`,
          params: { target_type: 'lodging' },
        })
      ).data,
    enabled: !!lodgingDoorId,
    retry: retryExcept404,
  })
}

export function useGetConvenience(
  convenienceId?: number
): UseQueryResult<ConvenienceChainProxy, Error> {
  return useQuery({
    queryKey: ['convenience', convenienceId],
    // FIR-7878: unified chain detail endpoint (byte-identical response).
    queryFn: async () =>
      (
        await client<ConvenienceChainProxy>({
          method: 'GET',
          url: `/api/v1/opportunities/detail/${convenienceId}/`,
          params: { target_type: 'cstore' },
        })
      ).data,
    enabled: !!convenienceId,
    retry: retryExcept404,
  })
}

export function useGetConvenienceDoor(
  convenienceDoorId?: number
): UseQueryResult<ConvenienceDoorProxy, Error> {
  return useQuery({
    queryKey: ['convenience', convenienceDoorId],
    // FIR-7878: unified door detail endpoint (byte-identical response).
    queryFn: async () =>
      (
        await client<ConvenienceDoorProxy>({
          method: 'GET',
          url: `/api/v1/opportunities/doors/detail/${convenienceDoorId}/`,
          params: { target_type: 'cstore' },
        })
      ).data,
    enabled: !!convenienceDoorId,
    retry: retryExcept404,
  })
}
